import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function CurrencyConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);

    // Memoize to avoid prop identity changes that could cause unnecessary rerenders in children
    const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

    // Centralize CRUD route to avoid string duplication and mistakes
    const CRUD_ROUTE = '/home/currencies/currencyConfigCrud';

   
    //=====================================================================================================================
    // openCurrencyCrud:
    // - Stable (useCallback) handler that encapsulates all CRUD-tab opening logic.
    // - Persists operation and, when required, the selected row into global state.
    // - Validates presence of 'data' for View/Edit/Copy, raising a user-friendly toast when missing.
    // - Computes the tab label from i18n keys to keep concerns local and avoid passing labels around.
    // - Using a stable callback prevents children (like MTable) from rerendering due to changing prop identities.
    //=====================================================================================================================
    const openCurrencyCrud = useCallback(
        (operation, data) => {
            // Persist operation for the CRUD screen
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

            // Ensure row is present for operations that need it
            if (['Edit', 'Copy', 'View'].includes(operation)) {
                if (!data) {
                    toastMessages('error', '', t('SelectedLine'));
                    return;
                }
                dispatch(M_StateActions.addM_State({ key: 'CurrencyLine', value: data }));
            }

            // Build tab label
            const tabLabel =
                operation === 'Create'
                    ? t('LabelNewCurrency')
                    : operation === 'View'
                    ? `${t('LabelViewCurrency')} "${data?.Name ?? ''}"`
                    : `${t('LabelEditCurrency')} "${data?.Name ?? ''}"`;

            // Open/focus CRUD tab
            onCreateTab(CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    //=====================================================================================================================
    // replaceFunctions:
    // - Memoized Map handed to MTable to resolve context actions (View/Create/Edit) to our stable handler.
    // - useMemo keeps identity stable across renders to avoid extra rerenders and accidental refetches.
    //=====================================================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openCurrencyCrud('View', row)],
                ['Create', () => openCurrencyCrud('Create')],
                ['Edit', (row) => openCurrencyCrud('Edit', row)],
            ]),
        [openCurrencyCrud]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div style={{ color: ctx.secondaryColor }} className="titleSettings">{t("Currencies")}</div>
                    <div className='labelSubtitleSettings'>{t("Manage the list of currencies in the system.")}</div>
                </div>
            </div>
            <MTable // Dynamic Table for displaying and managing currency data
                filtersOnTable
                entityName={'Currencies'}
                roleName={'Locations'}
                rolePermissions={roles?.map(vl => vl.Value)} // User roles for permission management in the Locations Module
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions} // replaceFunctions for CRUD operations
            />
        </div>
    );
}

CurrencyConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
